import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import styles from "./WorkBagSuccess.module.scss";
import { connect } from "react-redux";
import Confetti from "react-confetti";
import { getAllWorkOffers, openIdentityModal } from "../../store/actions";
import {
    MapPin,
    Wallet,
    Clock,
    Mail,
    FileText,
    ArrowRight,
    MessageSquare,
    Search,
    ShieldCheck,
    LogIn,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
} from "lucide-react";
import { fireAnalytics } from "../../utils/firebase";
import { useNavigate } from "react-router-dom";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import WorkApplyModal from "../WorkBag/WorkApplyModal.tsx";
import ApplyExternalJobOffer from "../WorkBag/ApplyExternalJobOffer.tsx";
import ModalOverlay from "../../components/UI/ModalOverlay/ModalOverlay.tsx";
import { isExternalWorkOffer } from "../../utils/jobboard/isExternalOffer.ts";

const OFFERS_PER_PAGE = 6;
const DESCRIPTION_MAX_LENGTH = 140;

interface WorkOfferUser {
    type?: string;
    username?: string;
    image_profile?: string;
    razon_social?: string;
    name?: string;
    last_name?: string;
}

interface WorkOfferItem {
    id: number;
    description?: string;
    createdAt?: string;
    activeUntil?: string;
    salary?: string;
    user?: WorkOfferUser;
    skill?: Array<{ skill?: { name?: string } }>;
    workZones?: Array<{ city?: string; jurisdiccion?: { name?: string } }>;
}

interface WorkBagSuccessProps {
    allJobRequests?: {
        workOffers?: WorkOfferItem[];
        total?: number;
    };
    getAllWorkOffers: () => void;
    openIdentityModal: (value: boolean, email: string) => void;
    userData?: {
        email?: string;
        username?: string;
        identity_validated?: boolean;
        jobBoard?: { identity_validated?: boolean };
    } | null;
}

const isExternalOffer = (workOffer: WorkOfferItem): boolean => {
    return isExternalWorkOffer(workOffer as any);
};

const trimDescription = (description?: string): string => {
    if (!description?.trim()) return "";
    const trimmed = description.trim();
    return trimmed.length > DESCRIPTION_MAX_LENGTH
        ? `${trimmed.substring(0, DESCRIPTION_MAX_LENGTH)}…`
        : trimmed;
};

const WorkBagSuccess: React.FC<WorkBagSuccessProps> = ({
    allJobRequests,
    getAllWorkOffers,
    openIdentityModal,
    userData,
}) => {
    const [showConfetti, setShowConfetti] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showExternalModal, setShowExternalModal] = useState(false);
    const [showIdentityPrompt, setShowIdentityPrompt] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState<WorkOfferItem | null>(null);

    const navigate = useNavigate();
    const offersListRef = useRef<HTMLDivElement>(null);
    const isInitialMount = useRef(true);

    const workOffers = [...(allJobRequests?.workOffers ?? [])].reverse();
    const userIdentityValidated = Boolean(
        userData?.jobBoard?.identity_validated ?? userData?.identity_validated ?? false
    );
    const totalOffers = workOffers.length;
    const totalPages = Math.max(1, Math.ceil(totalOffers / OFFERS_PER_PAGE));
    const startIndex = (currentPage - 1) * OFFERS_PER_PAGE;
    const paginatedOffers = workOffers.slice(startIndex, startIndex + OFFERS_PER_PAGE);

    useEffect(() => {
        setCurrentPage(1);
    }, [totalOffers]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        offersListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [currentPage]);

    useEffect(() => {
        getAllWorkOffers();
        fireAnalytics("page_work_bag_success_view", {
            session_id: getSessionId(),
            source: "onboarding",
            previous_page: "/bolsadetrabajo",
            device_category: getDeviceCategory(),
            category: "navigation",
            action: "page_view",
            page_name: "work_bag_success",
        });
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        const timer = setTimeout(() => setShowConfetti(false), 5000);
        return () => clearTimeout(timer);
    }, [getAllWorkOffers]);

    const calcularDiferenciaTiempo = (fechaCreacion: string): string => {
        const fechaActual = new Date();
        const fechaCreacionPost = new Date(fechaCreacion);
        const fechaCreacionLocal = new Date(
            fechaCreacionPost.getTime() -
                fechaCreacionPost.getTimezoneOffset() * 60000 -
                3 * 60 * 60 * 1000
        );
        let diferencia = fechaActual.getTime() - fechaCreacionLocal.getTime();
        if (diferencia < 0) diferencia = -diferencia;

        const minutos = Math.floor(diferencia / (1000 * 60));
        const horas = Math.floor(diferencia / (1000 * 60 * 60));
        const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));
        const semanas = Math.floor(dias / 7);
        const meses = Math.floor(dias / 30);

        if (minutos < 1) return "Ahora";
        if (minutos < 60) return minutos === 1 ? "1 minuto" : `${minutos} minutos`;
        if (horas < 24) return horas === 1 ? "1 hora" : `${horas} horas`;
        if (dias < 7) return dias === 1 ? "1 día" : `${dias} días`;
        if (semanas < 4) return semanas === 1 ? "1 semana" : `${semanas} semanas`;
        if (meses < 12) return meses === 1 ? "1 mes" : `${meses} meses`;
        const años = Math.floor(dias / 365);
        return años === 1 ? "1 año" : `${años} años`;
    };

    const formatStartDate = (date?: string): string => {
        if (!date) return "";
        const parts = date.split("T")[0].split("-");
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    };

    const openOfferFlow = (workOffer: WorkOfferItem): void => {
        setSelectedOffer(workOffer);
        if (isExternalOffer(workOffer)) {
            setShowExternalModal(true);
        } else {
            setShowApplyModal(true);
        }
    };

    const handleApply = (workOffer: WorkOfferItem): void => {
        setSelectedOffer(workOffer);
        if (!userIdentityValidated) {
            fireAnalytics("jobboard_identity_prompt_shown", {
                category: "onboarding",
                session_id: getSessionId(),
                device_category: getDeviceCategory(),
                section: "jobboard_public",
                action: "identity_prompt_shown",
                offer_id: workOffer.id,
                offer_type: isExternalOffer(workOffer) ? "external" : "internal",
            });
            setShowIdentityPrompt(true);
            return;
        }
        openOfferFlow(workOffer);
    };

    const handleRegisterClick = (): void => {
        navigate("/");
    };

    const handleIdentityPromptValidateNow = (): void => {
        const email = (userData?.email || userData?.username || localStorage.getItem("EmailRegister") || "").toLowerCase();
        fireAnalytics("jobboard_identity_prompt_action", {
            category: "onboarding",
            session_id: getSessionId(),
            device_category: getDeviceCategory(),
            section: "jobboard_public",
            action: "identity_prompt_validate_now",
            offer_id: selectedOffer?.id ?? "",
            offer_type: selectedOffer && isExternalOffer(selectedOffer) ? "external" : "internal",
        });
        setShowIdentityPrompt(false);
        openIdentityModal(true, email);
    };

    const handleIdentityPromptContinue = (): void => {
        if (!selectedOffer) return;
        fireAnalytics("jobboard_identity_prompt_action", {
            category: "onboarding",
            session_id: getSessionId(),
            device_category: getDeviceCategory(),
            section: "jobboard_public",
            action: "identity_prompt_continue_without_validation",
            offer_id: selectedOffer.id,
            offer_type: isExternalOffer(selectedOffer) ? "external" : "internal",
        });
        setShowIdentityPrompt(false);
        openOfferFlow(selectedOffer);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={styles.workBagSuccess}
        >
            {showConfetti && (
                <div className={styles.confettiContainer}>
                    <Confetti />
                </div>
            )}
            <header className={styles.workBagSuccess_header}>
                <div className={styles.workBagSuccess_header_container}>
                    <div className={styles.workBagSuccess_title}>
                        <div className={styles.workBagSuccess_title_text}>
                            <h1 className={styles.workBagSuccess_title_text_h1}>
                                ¡Bienvenido a nuestra bolsa de trabajo!
                            </h1>
                            <p className={styles.workBagSuccess_title_text_p}>
                                Tu registro ha sido exitoso. A partir de ahora, nuestro sistema buscará las mejores oportunidades
                        laborales basadas en tu perfil. Cuando encontremos ofertas que coincidan
                        con tus habilidades y ubicación, te notificaremos por email.
                            </p>
                        </div>
                    </div>
                    <div className={styles.workBagSuccess_ctaBlock}>
                        <p className={styles.workBagSuccess_ctaBlock_title}>
                            Iniciá sesión para activar tu período gratis
                        </p>
                        <p className={styles.workBagSuccess_ctaBlock_text}>
                            Ya tenés tu cuenta creada. Iniciá sesión con el email con el que te registraste
                            para poder activar tu free trial de 60 días y puedas acceder a las
                            funcionalidades premium.
                        </p>
                        <button
                            type="button"
                            className={styles.workBagSuccess_ctaBlock_btn}
                            onClick={handleRegisterClick}
                        >
                            <LogIn size={20} />
                            Ir al inicio de sesión
                        </button>
                    </div>
                </div>
            </header>
            <div className={styles.workBagSuccess_body}>
                <div className={styles.workBagSuccess_body_infoContainer}>
                    <div className={styles.workBagSuccess_body_infoContainer_container}>
                        <h4 className={styles.workBagSuccess_body_infoContainer_title}>
                            Como funciona el proceso
                        </h4>
                        <div className={styles.workBagSuccess_body_infoContainer_stepContainer}>
                            <div className={styles.workBagSuccess_body_infoContainer_step}>
                                <div className={styles.workBagSuccess_body_infoContainer_step_icon}>
                                    <Mail size={24} />
                                </div>
                                <h5 className={styles.workBagSuccess_body_infoContainer_step_title}>
                                    1. Iniciá sesión y explorá ofertas
                                </h5>
                                <p className={styles.workBagSuccess_body_infoContainer_step_text}>
                                    Ya podés entrar con tu email y contraseña para ver las ofertas que
                                    tenemos disponibles para vos.
                                </p>
                            </div>
                            <div className={styles.workBagSuccess_body_infoContainer_step}>
                                <div className={styles.workBagSuccess_body_infoContainer_step_icon}>
                                    <ShieldCheck size={24} />
                                </div>
                                <h5 className={styles.workBagSuccess_body_infoContainer_step_title}>
                                    2. Completá lo que falte para postular
                                </h5>
                                <p className={styles.workBagSuccess_body_infoContainer_step_text}>
                                    Si la plataforma te lo solicita, completá la validación de identidad
                                    y/o tu perfil de bolsa para poder postular sin inconvenientes.
                                </p>
                            </div>
                            <div className={styles.workBagSuccess_body_infoContainer_step}>
                                <div className={styles.workBagSuccess_body_infoContainer_step_icon}>
                                    <FileText size={24} />
                                </div>
                                <h5 className={styles.workBagSuccess_body_infoContainer_step_title}>
                                    3. Postulá en pocos pasos
                                </h5>
                                <p className={styles.workBagSuccess_body_infoContainer_step_text}>
                                    Al postular, subí tu CV y escribí una carta de presentación
                                    específica para la oferta. Si la oferta es externa, Edify
                                    acercará tu propuesta a la empresa.
                                </p>
                            </div>
                            <div className={styles.workBagSuccess_body_infoContainer_step}>
                                <div className={styles.workBagSuccess_body_infoContainer_step_icon}>
                                    <MessageSquare size={24} />
                                </div>
                                <h5 className={styles.workBagSuccess_body_infoContainer_step_title}>
                                    4. Seguimiento y contacto
                                </h5>
                                <p className={styles.workBagSuccess_body_infoContainer_step_text}>
                                    Si tu perfil coincide, la empresa puede contactarte para continuar
                                    el proceso. En ofertas externas, Edify hace todo lo posible por
                                    acercar tu propuesta, sin garantizar respuesta o contratación.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    ref={offersListRef}
                    className={styles.workBagSuccess_body_workOfferContainer}
                >
                    <div className={styles.workBagSuccess_body_workOfferContainer_container}>
                        <h4 className={styles.workBagSuccess_body_workOfferContainer_title}>
                            Ofertas de trabajo disponibles
                        </h4>
                        <div className={styles.workBagSuccess_body_workOfferContainer_disclaimer}>
                            <div
                                className={
                                    styles.workBagSuccess_body_workOfferContainer_disclaimer_icon
                                }
                            >
                                <ShieldCheck size={24} />
                            </div>
                            <div
                                className={
                                    styles.workBagSuccess_body_workOfferContainer_disclaimer_text
                                }
                            >
                                <div
                                    className={
                                        styles.workBagSuccess_body_workOfferContainer_disclaimer_text_title
                                    }
                                >
                                    Validación de identidad opcional
                                </div>
                                <p
                                    className={
                                        styles.workBagSuccess_body_workOfferContainer_disclaimer_text_p
                                    }
                                >
                                    Podés completar el proceso de validación de identidad para
                                    fortalecer tu perfil y generar más confianza al postularte.
                                    El proceso es opcional y podés hacerlo ahora o más adelante.
                                </p>
                            </div>
                        </div>
                        <div className={styles.workBagSuccess_body_workOfferContainer_workOffer}>
                            {totalOffers > 0 ? (
                                paginatedOffers.map((jobRequest) => {
                                    const nameToDisplay =
                                        jobRequest.user?.type === "COMPANY"
                                            ? jobRequest.user?.razon_social
                                            : [jobRequest.user?.name, jobRequest.user?.last_name]
                                                  .filter(Boolean)
                                                  .join(" ");
                                    const skillNameToDisplay =
                                        jobRequest.skill?.[0]?.skill?.name ?? "";
                                    const cityToDisplay = jobRequest.workZones?.[0]?.city;
                                    const activeUntilToDisplay = jobRequest.activeUntil
                                        ? formatStartDate(jobRequest.activeUntil)
                                        : "";
                                    const createdAtToDisplay = jobRequest?.createdAt?.split("T")[0] ?? "";
                                    const descriptionPreview = trimDescription(
                                        jobRequest.description
                                    );

                                    const railPrimary =
                                        activeUntilToDisplay
                                            ? {
                                                  label: "Plazo para postular",
                                                  value: `Hasta el ${activeUntilToDisplay}`,
                                              }
                                            : createdAtToDisplay
                                              ? {
                                                    label: "Publicada",
                                                    value: `Hace ${calcularDiferenciaTiempo(createdAtToDisplay)}`,
                                                }
                                              : {
                                                    label: "Vigencia",
                                                    value: "Sin fecha de cierre",
                                                };

                                    return (
                                        <div
                                            className={
                                                styles.workBagSuccess_body_workOfferContainer_workOffer_card
                                            }
                                            key={jobRequest.id}
                                        >
                                            {isExternalOffer(jobRequest) ? (
                                                <span
                                                    className={
                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_replicatedBadge
                                                    }
                                                >
                                                    <ExternalLink size={12} />
                                                    Aviso replicado
                                                </span>
                                            ) : null}
                                            <div
                                                className={
                                                    styles.workBagSuccess_body_workOfferContainer_workOffer_card_container
                                                }
                                            >
                                                <aside
                                                    className={
                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_rail
                                                    }
                                                    data-variant={isExternalOffer(jobRequest) ? "external" : "internal"}
                                                >
                                                    <div
                                                        className={
                                                            styles.workBagSuccess_body_workOfferContainer_workOffer_card_railTop
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.workBagSuccess_body_workOfferContainer_workOffer_card_railLabel
                                                            }
                                                        >
                                                            {railPrimary.label}
                                                        </div>
                                                        <div
                                                            className={
                                                                styles.workBagSuccess_body_workOfferContainer_workOffer_card_railValue
                                                            }
                                                        >
                                                            {railPrimary.value}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.workBagSuccess_body_workOfferContainer_workOffer_card_railTag
                                                        }
                                                    >
                                                        {isExternalOffer(jobRequest) ? (
                                                            <>
                                                                <span
                                                                    className={
                                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_railTagTitle
                                                                    }
                                                                >
                                                                    Búsqueda externa
                                                                </span>
                                                                <p
                                                                    className={
                                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_railTagNote
                                                                    }
                                                                >
                                                                    Edify replica este aviso y hará todo lo posible por acercar tu propuesta a quien publicó.
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span
                                                                    className={
                                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_railTagTitle
                                                                    }
                                                                >
                                                                    Bolsa Edify
                                                                </span>
                                                                <p
                                                                    className={
                                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_railTagNote
                                                                    }
                                                                >
                                                                    Postulación y seguimiento desde la plataforma.
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </aside>

                                                <div
                                                    className={
                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_main
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.workBagSuccess_body_workOfferContainer_workOffer_card_headerRow
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.workBagSuccess_body_workOfferContainer_workOffer_card_logo
                                                            }
                                                        >
                                                            {jobRequest.user?.image_profile ? (
                                                                <img
                                                                    src={jobRequest.user.image_profile}
                                                                    alt={nameToDisplay}
                                                                />
                                                            ) : (
                                                                <span>
                                                                    {nameToDisplay?.trim()?.[0]
                                                                        ? nameToDisplay.trim()[0].toUpperCase()
                                                                        : ""}
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div
                                                            className={
                                                                styles.workBagSuccess_body_workOfferContainer_workOffer_card_headText
                                                            }
                                                        >
                                                            {skillNameToDisplay ? (
                                                                <div
                                                                    className={
                                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_skill
                                                                    }
                                                                >
                                                                    {skillNameToDisplay}
                                                                </div>
                                                            ) : null}
                                                            <div
                                                                className={
                                                                    styles.workBagSuccess_body_workOfferContainer_workOffer_card_companyLine
                                                                }
                                                            >
                                                                <span
                                                                    className={
                                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_companyName
                                                                    }
                                                                >
                                                                    {nameToDisplay}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {descriptionPreview ? (
                                                        <p
                                                            className={
                                                                styles.workBagSuccess_body_workOfferContainer_workOffer_card_description
                                                            }
                                                        >
                                                            {descriptionPreview}
                                                        </p>
                                                    ) : null}

                                                    <div
                                                        className={
                                                            styles.workBagSuccess_body_workOfferContainer_workOffer_card_metaRow
                                                        }
                                                    >
                                                        {cityToDisplay ? (
                                                            <span
                                                                className={
                                                                    styles.workBagSuccess_body_workOfferContainer_workOffer_card_metaItem
                                                                }
                                                            >
                                                                <MapPin size={16} />
                                                                {cityToDisplay}
                                                            </span>
                                                        ) : null}

                                                        {createdAtToDisplay ? (
                                                            <span
                                                                className={
                                                                    styles.workBagSuccess_body_workOfferContainer_workOffer_card_metaItem
                                                                }
                                                            >
                                                                <Clock size={16} />
                                                                Hace {calcularDiferenciaTiempo(createdAtToDisplay)}
                                                            </span>
                                                        ) : null}

                                                        {jobRequest.salary ? (
                                                            <span
                                                                className={
                                                                    styles.workBagSuccess_body_workOfferContainer_workOffer_card_metaItem
                                                                }
                                                            >
                                                                <Wallet size={16} />
                                                                {jobRequest.salary}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div
                                                    className={
                                                        styles.workBagSuccess_body_workOfferContainer_workOffer_card_action
                                                    }
                                                >
                                                    <button
                                                        className={
                                                            styles.workBagSuccess_body_workOfferContainer_workOffer_card_btn
                                                        }
                                                        onClick={() => handleApply(jobRequest)}
                                                    >
                                                        {"Postularme"}{" "}
                                                        <ArrowRight size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div
                                    className={
                                        styles.workBagSuccess_body_workOfferContainer_noWorkOffer
                                    }
                                >
                                    <div
                                        className={
                                            styles.workBagSuccess_body_workOfferContainer_noWorkOffer_icon
                                        }
                                    >
                                        <Search size={50} />
                                    </div>
                                    <h5
                                        className={
                                            styles.workBagSuccess_body_workOfferContainer_noWorkOffer_title
                                        }
                                    >
                                        ¡No hay ofertas disponibles!
                                    </h5>
                                    <p
                                        className={
                                            styles.workBagSuccess_body_workOfferContainer_noWorkOffer_text
                                        }
                                    >
                                        Estamos buscando las mejores oportunidades que coincidan
                                        con tus habilidades y experiencia. Te notificaremos por
                                        email cuando encontremos ofertas relevantes.
                                    </p>
                                </div>
                            )}
                            {totalOffers > OFFERS_PER_PAGE && (
                                <div
                                    className={
                                        styles.workBagSuccess_body_workOfferContainer_pagination
                                    }
                                >
                                    <button
                                        type="button"
                                        className={
                                            styles.workBagSuccess_body_workOfferContainer_pagination_btn
                                        }
                                        onClick={() =>
                                            setCurrentPage((p) => Math.max(1, p - 1))
                                        }
                                        disabled={currentPage === 1}
                                        aria-label="Página anterior"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    {Array.from(
                                        { length: totalPages },
                                        (_, i) => i + 1
                                    ).map((page) => (
                                        <button
                                            key={page}
                                            type="button"
                                            className={`${styles.workBagSuccess_body_workOfferContainer_pagination_btn} ${currentPage === page ? styles.active : ""}`}
                                            onClick={() => setCurrentPage(page)}
                                            aria-label={`Página ${page}`}
                                            aria-current={
                                                currentPage === page ? "page" : undefined
                                            }
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className={
                                            styles.workBagSuccess_body_workOfferContainer_pagination_btn
                                        }
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.min(totalPages, p + 1)
                                            )
                                        }
                                        disabled={currentPage === totalPages}
                                        aria-label="Página siguiente"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                    <p
                                        className={
                                            styles.workBagSuccess_body_workOfferContainer_pagination_info
                                        }
                                    >
                                        {startIndex + 1}-
                                        {Math.min(
                                            startIndex + OFFERS_PER_PAGE,
                                            totalOffers
                                        )}{" "}
                                        de {totalOffers} ofertas
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showApplyModal && selectedOffer && (
                <WorkApplyModal
                    workOffer={selectedOffer as any}
                    setShowApplyModal={setShowApplyModal}
                    setShowForm={() => navigate("/bolsadetrabajo")}
                    forbiddenClose={false}
                    skipWorkBagUserCheck={false}
                    prefilledEmail={undefined}
                    identityValidated={undefined}
                />
            )}
            {showExternalModal && selectedOffer && (
                <ApplyExternalJobOffer
                    workOffer={selectedOffer as any}
                    isOpen={showExternalModal}
                    onClose={() => {
                        setShowExternalModal(false);
                        setSelectedOffer(null);
                    }}
                />
            )}
            {showIdentityPrompt && selectedOffer && (
                <ModalOverlay
                    isOpen={true}
                    onClose={() => setShowIdentityPrompt(false)}
                >
                    <div className={styles.identityPromptModal} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.identityPromptTitle}>Validar identidad</h3>
                        <p className={styles.identityPromptText}>
                            Podés validar tu identidad ahora para destacar tu perfil, o continuar sin validar y hacerlo más adelante.
                        </p>
                        <div className={styles.identityPromptActions}>
                            <button
                                type="button"
                                className={styles.identityPromptSecondary}
                                onClick={handleIdentityPromptContinue}
                            >
                                Continuar sin validar
                            </button>
                            <button
                                type="button"
                                className={styles.identityPromptPrimary}
                                onClick={handleIdentityPromptValidateNow}
                            >
                                Validar ahora
                            </button>
                        </div>
                    </div>
                </ModalOverlay>
            )}
        </motion.div>
    );
};

const mapStateToProps = (state: {
    register?: { allJobRequests?: WorkBagSuccessProps["allJobRequests"] };
    general?: { userData?: WorkBagSuccessProps["userData"] };
}) => ({
    allJobRequests: state.register?.allJobRequests,
    userData: state.general?.userData,
});

export default connect(mapStateToProps, { getAllWorkOffers, openIdentityModal })(WorkBagSuccess);
